<template>
  <span ref="root" class="tabular">{{ display }}</span>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    to: number;
    suffix?: string;
    /** Duration in ms. Default 900 (count-up token). */
    dur?: number;
    /** When true, force-render integer; default auto-detects. */
    integer?: boolean;
    /** Delay in ms before animation starts. */
    delay?: number;
  }>(),
  { suffix: "", dur: 550, delay: 0 },
);

const root = ref<HTMLElement | null>(null);
const display = ref<string>("");
let started = false;
let raf = 0;

function format(v: number): string {
  const isInt = props.integer ?? Number.isInteger(props.to);
  return (isInt ? Math.round(v).toString() : v.toFixed(1)) + props.suffix;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Skip the count-up in test runs and when the user prefers reduced motion.
 *  Test runners (vitest) freeze tick-style animations behind real timers,
 *  so leaving the target value rendered keeps assertions deterministic. */
function shouldSnap(): boolean {
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  if (reduced || props.dur <= 0) return true;
  // import.meta.env exists in Vite; vitest sets MODE === "test".
  const env = (import.meta as ImportMeta).env as { MODE?: string; VITEST?: boolean } | undefined;
  if (env?.MODE === "test" || env?.VITEST) return true;
  return false;
}

function animate(): void {
  if (started) return;
  started = true;
  if (shouldSnap()) {
    display.value = format(props.to);
    return;
  }
  const start = performance.now() + (props.delay || 0);
  function tick(now: number): void {
    if (now < start) {
      raf = requestAnimationFrame(tick);
      return;
    }
    const t = Math.min(1, (now - start) / props.dur);
    display.value = format(easeOutCubic(t) * props.to);
    if (t < 1) raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);
}

onMounted(() => {
  // Initial render: zero (so the count-up has visible runway), unless we're
  // snapping — then jump straight to the final value.
  display.value = shouldSnap() ? format(props.to) : format(0);
  animate();
});

onUnmounted(() => { if (raf) cancelAnimationFrame(raf); });

watch(
  () => props.to,
  () => {
    started = false;
    animate();
  },
);
</script>
